import { createContext, useContext, useState, useMemo, type ReactNode } from "react";
import { analyticsLastDays as daysAgoInTz, analyticsComparisonPeriod as computeComparisonPeriod } from "@shared/analyticsDates";
import { useAuthUser } from "@/App";
import { useDashboardAccounts } from "@/contexts/DashboardAccountContext";

export interface DatePreset {
  label: string;
  key: string;
  getDates: () => { startDate: string; endDate: string };
}

interface DashboardDateContextType {
  startDate: string;
  endDate: string;
  comparisonStartDate: string;
  comparisonEndDate: string;
  presetKey: string;
  setPreset: (key: string) => void;
  setCustomRange: (start: string, end: string) => void;
  presets: DatePreset[];
  timezone: string;
  timezoneAbbr: string;
}

function formatDateInTz(date: Date, tz: string): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(date);
}

function getTimezoneAbbr(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const tzPart = parts.find(p => p.type === "timeZoneName");
    return tzPart?.value || tz;
  } catch {
    return tz;
  }
}

function todayInTz(tz: string): string {
  return formatDateInTz(new Date(), tz);
}

function buildPresets(tz: string): DatePreset[] {
  return [
    { label: "Today", key: "today", getDates: () => { const d = todayInTz(tz); return { startDate: d, endDate: d }; } },
    { label: "Yesterday", key: "yesterday", getDates: () => {
      const today = todayInTz(tz);
      const parts = today.split("-").map(Number);
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      d.setDate(d.getDate() - 1);
      const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return { startDate: s, endDate: s };
    }},
    { label: "Last 7 days", key: "7d", getDates: () => daysAgoInTz(7, tz) },
    { label: "Last 14 days", key: "14d", getDates: () => daysAgoInTz(14, tz) },
    { label: "Last 30 days", key: "30d", getDates: () => daysAgoInTz(30, tz) },
    { label: "Last 90 days", key: "90d", getDates: () => daysAgoInTz(90, tz) },
  ];
}

const DashboardDateContext = createContext<DashboardDateContextType | null>(null);

export function DashboardDateProvider({ children }: { children: ReactNode }) {
  const user = useAuthUser();
  const { allAccounts } = useDashboardAccounts();
  const accountTz = allAccounts.length > 0 ? allAccounts[0].timezone : null;
  const tz = user?.timezone || accountTz || "UTC";
  const timezoneAbbr = useMemo(() => getTimezoneAbbr(tz), [tz]);

  const presets = useMemo(() => buildPresets(tz), [tz]);

  const [presetKey, setPresetKey] = useState("30d");
  const [customRange, setCustomRangeState] = useState<{ startDate: string; endDate: string } | null>(null);

  const { startDate, endDate } = useMemo(() => {
    if (customRange) return customRange;
    const preset = presets.find(p => p.key === presetKey);
    return preset ? preset.getDates() : daysAgoInTz(30, tz);
  }, [presetKey, customRange, presets, tz]);

  const { comparisonStartDate, comparisonEndDate } = useMemo(
    () => computeComparisonPeriod(startDate, endDate),
    [startDate, endDate]
  );

  const setPreset = (key: string) => {
    setPresetKey(key);
    setCustomRangeState(null);
  };

  const setCustomRange = (start: string, end: string) => {
    setPresetKey("custom");
    setCustomRangeState({ startDate: start, endDate: end });
  };

  return (
    <DashboardDateContext.Provider value={{
      startDate,
      endDate,
      comparisonStartDate,
      comparisonEndDate,
      presetKey,
      setPreset,
      setCustomRange,
      presets,
      timezone: tz,
      timezoneAbbr,
    }}>
      {children}
    </DashboardDateContext.Provider>
  );
}

function getFallbackDashboardDate(): DashboardDateContextType {
  const { startDate, endDate } = daysAgoInTz(30, "UTC");
  const { comparisonStartDate, comparisonEndDate } = computeComparisonPeriod(startDate, endDate);
  return {
    startDate,
    endDate,
    comparisonStartDate,
    comparisonEndDate,
    presetKey: "30d",
    setPreset: () => {},
    setCustomRange: () => {},
    presets: buildPresets("UTC"),
    timezone: "UTC",
    timezoneAbbr: "UTC",
  };
}

export function useDashboardDate(): DashboardDateContextType {
  const ctx = useContext(DashboardDateContext);
  if (!ctx) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("useDashboardDate: called outside DashboardDateProvider — returning fallback dates.");
    }
    return getFallbackDashboardDate();
  }
  return ctx;
}
